import React, { useState, useEffect } from "react";
import axiosInstance from "../../../api/axiosInstance";
import { FaArrowLeft, FaFileInvoice, FaPlus, FaCheck, FaClock, FaTimes } from "react-icons/fa";
import { showGlobalToast } from "../../../utils/toast";

interface OrderItem {
    id: string;
    name: string;
    quantity: number;
    instructions?: string;
    status: 'processing' | 'delivered' | 'cancelled';
}

interface Order {
    orderId: string;
    orderTime: string;
    items: OrderItem[];
}

type CustomerDetails = {
    id?: string;
    customerName?: string;
    customerEmail?: string;
    customerMobile?: string;
    customerCount?: number;
    waiterName?: string;
};

interface TableOrderDetailsProps {
    tableId: string;
    tableLabel: string;
    onBack: () => void;
    onBill: () => void;
    onNewOrder: (details: CustomerDetails) => void;
}

const TableOrderDetails: React.FC<TableOrderDetailsProps> = ({
    tableId,
    tableLabel,
    onBack,
    onBill,
    onNewOrder
}) => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [customerDetails, setCustomerDetails] = useState<CustomerDetails | null>(null);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                setLoading(true);
                // Use the bill API to get order details for the table
                const response = await axiosInstance.get(`/Billing/GetBillDetails/table/${tableId}`);
                // Transform the bill data to order format
                const billData = response.data;
                if (billData && billData.orders) {
                    const transformedOrders: Order[] = billData.orders.map((order: any) => ({
                        orderId: order.orderId,
                        orderTime: order.orderTime,
                        items: order.items.map((item: any) => ({
                            id: item.id || `${order.orderId}-${item.name}`,
                            name: item.name,
                            quantity: item.qty || item.quantity,
                            instructions: item.instructions || '',
                            status: item.status || 'processing'
                        }))
                    }));
                    setOrders(transformedOrders);
                    setCustomerDetails({
                        id: billData.customerId || '',
                        customerName: billData.customerName || 'Guest',
                        customerEmail: billData.customerEmail || '',
                        customerMobile: billData.customerMobile || '',
                        customerCount: billData.customerCount || 1,
                        waiterName: billData.waiterName || '',
                    });
                } else {
                    setOrders([]);
                    setCustomerDetails(null);
                }
            } catch (error: any) {
                console.error("Error fetching order details:", error);
                showGlobalToast(error.response?.data?.message || "Error fetching order details", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchOrderDetails();
    }, [tableId]);

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'delivered':
                return <FaCheck className="text-green-500" size={16} />;
            case 'cancelled':
                return <FaTimes className="text-red-500" size={16} />;
            case 'processing':
            default:
                return <FaClock className="text-yellow-500" size={16} />;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading order details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
            {/* Header */}
            <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <button
                            onClick={onBack}
                            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            <FaArrowLeft className="mr-2" size={20} />
                            <span className="font-medium">Back</span>
                        </button>
                        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                            Order Details
                        </h1>
                        <div className="w-20"></div> {/* Spacer for centering */}
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Table Label */}
                <div className="mb-6">
                    <h2 className="text-sm sm:text-base font-light text-gray-600 dark:text-gray-400">
                        Order Details for {tableLabel}
                    </h2>
                </div>

                {/* Orders */}
                <div className="space-y-4">
                    {orders.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500 dark:text-gray-400">No orders found for this table.</p>
                        </div>
                    ) : (
                        orders.map((order) => (
                            <div
                                key={order.orderId}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                            >
                                {/* Order Header */}
                                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            Order #{order.orderId}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {new Date(order.orderTime).toLocaleString()}
                                        </span>
                                    </div>
                                </div>

                                {/* Order Items */}
                                <div className="divide-y divide-gray-200 dark:divide-gray-600">
                                    {order.items.map((item, index) => (
                                        <div key={index} className="px-4 py-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-3">
                                                        <span className="font-medium text-gray-900 dark:text-white">
                                                            {item.name}
                                                        </span>
                                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                                            Qty: {item.quantity}
                                                        </span>
                                                        {getStatusIcon(item.status)}
                                                    </div>
                                                    {item.instructions && (
                                                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 italic">
                                                            Note: {item.instructions}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Bottom Buttons */}
                <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-4 sm:px-6">
                    <div className="max-w-7xl mx-auto flex space-x-4">
                        <button
                            onClick={onBill}
                            className="flex-1 flex items-center justify-center px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
                        >
                            <FaFileInvoice className="mr-2" size={18} />
                            Bill
                        </button>
                        <button
                            onClick={() => onNewOrder(customerDetails || { customerName: 'Guest' })}
                            className="flex-1 flex items-center justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                        >
                            <FaPlus className="mr-2" size={18} />
                            New Order
                        </button>
                    </div>
                </div>

                {/* Bottom Spacer */}
                <div className="h-20"></div>
            </main>
        </div>
    );
};

export default TableOrderDetails;